/* eslint-disable no-unused-vars */
import debugFactory from "debug";
import orderBy from "lodash/orderBy";
import sampleSize from "lodash/sampleSize";

import { alertFactory } from "@teaming/common-ui";

import { configureChannels, configureSockets } from "./listeners";
import { pristineState } from "./state";

export default function workStylesStoreFactory( {
	storeName,
	foundation,
	appChannels,
} ) {
	// eslint-disable-next-line no-unused-vars
	const debug = debugFactory( `teaming:common-team:stores:${ storeName }` );

	const { store, navigation, i18n, ioSocket, ioMeeting, ioTeam, ioUser, telemetry, } = foundation();
	const { t, } = i18n.global;
	const { ioChannel, } = appChannels;

	const { success, error, info, } = alertFactory( "common-team.stores.WorkStyles", t );
	const alerts = { success, error, info, };

	const getUserProfile = () => store.getters[ "host-userProfile/userProfile" ];

	configureSockets( {
		storeName,
		store,
		navigation,
		ioSocket,
		ioTeam,
		alerts,
	} );

	configureChannels( {
		storeName,
		store,
		navigation,
		ioChannel,
		ioMeeting,
		alerts,
	} );

	return {
		namespaced: true,
		state: pristineState(),
		getters: {
			userProfile: () => {
				return getUserProfile();
			},
			team: state => state.team,
			teamType: state => state.team.type,
			activePanel: state => state.activePanel,
			isWorkStyleDialogVisible: state => state.isWorkStyleDialogVisible,
			assessments( state, getters ) {
				const { userProfile, team, } = getters;
				const { personId: currentUserId, } = userProfile;

				if ( !getters.team?.id ) {
					return [];
				}

				const discAssessments = team.members.map( member => {
					const { personId, assessments, } = member;

					const discAssessment = assessments?.DISC || {
						moniker: "",
						teamingContent: {
							descriptors: [],
						},
					};
					const { moniker, teamingContent: { descriptors, }, } = discAssessment;
					const highType = moniker.slice( 0, 1 ).toUpperCase();

					return {
						uiKey: personId,
						sortKey: member.displayName.toLowerCase(),
						highType,
						moniker,
						// eslint-disable-next-line no-magic-numbers
						featuredQualities: sampleSize( descriptors, 3 ),
						isComplete: !!moniker,
						...discAssessment,
						member,
						forCurrentUser: ( personId === currentUserId ),
					};
				} );

				return orderBy( discAssessments, [ "sortKey", ], [ "asc", ] );
			},
		},
		mutations: {
			resetStoreState( state, { team, } ) {
				Object.assign( state, pristineState( i18n ) );

				if ( team ) {
					state.team = team;
				}
			},
			receiveTeam( state, team ) {
				state.team = team;
			},
			setActivePanel( state, activePanel ) {
				state.activePanel = activePanel;
			},
			showWorkStyleDialog( state ) {
				state.isWorkStyleDialogVisible = true;
			},
			hideWorkStyleDialog( state ) {
				state.isWorkStyleDialogVisible = false;
			},
		},
		actions: {
			async initialize( { commit, }, { teamId = "", } ) {
				try {
					// TODO: we shouldn't have to refetch here, but if a team is loaded
					// elsewhere in the app *without* work styles, then they won't show up
					// here. We need to add a flag to the fetch call to indicate whether to
					// include work styles or not, which will be stored as part of the cache
					// key and give us expected results. Until then we're just going
					// to force a refetch.
					// @see also the refreshTeam() action
					const teamResponse = await ioTeam.fetchTeam( { teamId, refetch: true, } );
					const team = teamResponse.data;
					commit( "resetStoreState", { team, } );
				} catch ( e ) {
					error( "unexpectedError", e );
				}
			},
			async refreshTeam( { state, commit, } ) {
				try {
					const { team, } = state;
					if ( !team.id ) {
						return;
					}
					const { id: teamId, } = team;
					const teamResponse = await ioTeam.fetchTeam( { teamId, refetch: true, } );
					const refreshedTeam = teamResponse.data;
					commit( "receiveTeam", refreshedTeam );
				} catch ( e ) {
					error( "unexpectedError", e );
				}
			},
		},
	};
}
